/**
 * MUI Theme Configuration
 *
 * @module shared/theme/theme
 *
 * Creates Material UI themes for dark and light modes,
 * extending MUI's default theme with AutoCollect's design system.
 */

import { createTheme, alpha } from '@mui/material/styles';

import { darkPalette, lightPalette } from './palette';

import type { ThemeOptions } from '@mui/material/styles';

// ============================================================================
// MUI Type Augmentation
// ============================================================================

declare module '@mui/material/styles' {
    interface TypeBackground {
        tableZebra?: string;
        tableSelected?: string;
    }
}

// ============================================================================
// Typography Configuration
// ============================================================================

const typography: ThemeOptions['typography'] = {
    fontFamily: '"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,

    h1: {
        fontSize: '2rem',
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
    },
    h2: {
        fontSize: '1.5rem',
        fontWeight: 700,
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
    },
    h3: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.4,
    },
    h4: {
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.4,
    },
    h5: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.5,
    },
    h6: {
        fontSize: '0.875rem',
        fontWeight: 600,
        lineHeight: 1.5,
    },
    subtitle1: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.5,
    },
    subtitle2: {
        fontSize: '0.75rem',
        fontWeight: 500,
        lineHeight: 1.5,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    },
    body1: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
    },
    body2: {
        fontSize: '0.75rem',
        lineHeight: 1.5,
    },
    caption: {
        fontSize: '0.6875rem',
        lineHeight: 1.4,
    },
    overline: {
        fontSize: '0.625rem',
        fontWeight: 600,
        lineHeight: 1.5,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    },
    button: {
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'none',
        letterSpacing: '0.01em',
    },

    // Consistent digit alignment across numeric columns.
    allVariants: {
        fontVariantNumeric: 'tabular-nums',
    },
};

// ============================================================================
// Shadows Configuration
// ============================================================================

const shadows: ThemeOptions['shadows'] = [
    'none',
    '0 1px 2px rgba(0, 0, 0, 0.05)',
    '0 1px 3px rgba(0, 0, 0, 0.1)',
    '0 2px 4px rgba(0, 0, 0, 0.1)',
    '0 4px 6px rgba(0, 0, 0, 0.1)',
    '0 6px 8px rgba(0, 0, 0, 0.1)',
    '0 8px 10px rgba(0, 0, 0, 0.12)',
    '0 10px 14px rgba(0, 0, 0, 0.12)',
    '0 12px 16px rgba(0, 0, 0, 0.14)',
    '0 14px 18px rgba(0, 0, 0, 0.14)',
    '0 16px 20px rgba(0, 0, 0, 0.15)',
    '0 18px 24px rgba(0, 0, 0, 0.15)',
    '0 20px 28px rgba(0, 0, 0, 0.16)',
    '0 22px 32px rgba(0, 0, 0, 0.16)',
    '0 24px 36px rgba(0, 0, 0, 0.17)',
    '0 26px 40px rgba(0, 0, 0, 0.17)',
    '0 28px 44px rgba(0, 0, 0, 0.18)',
    '0 30px 48px rgba(0, 0, 0, 0.18)',
    '0 32px 52px rgba(0, 0, 0, 0.19)',
    '0 34px 56px rgba(0, 0, 0, 0.19)',
    '0 36px 60px rgba(0, 0, 0, 0.2)',
    '0 38px 64px rgba(0, 0, 0, 0.2)',
    '0 40px 68px rgba(0, 0, 0, 0.21)',
    '0 42px 72px rgba(0, 0, 0, 0.21)',
    '0 10px 40px rgba(0, 0, 0, 0.5)', // elevation 24 - Dialog shadow
];

// ============================================================================
// Component Overrides
// ============================================================================

const getComponentOverrides = (mode: 'light' | 'dark'): NonNullable<ThemeOptions['components']> => {
    const palette = mode === 'dark' ? darkPalette : lightPalette;

    return {
        MuiCssBaseline: {
            styleOverrides: {
                '*': {
                    boxSizing: 'border-box',
                },
                html: {
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                },
                body: {
                    margin: 0,
                    backgroundColor: palette.background.default,
                    fontVariantNumeric: 'tabular-nums',
                },
                '::-webkit-scrollbar': {
                    width: '6px',
                    height: '6px',
                },
                '::-webkit-scrollbar-track': {
                    background: 'transparent',
                },
                '::-webkit-scrollbar-thumb': {
                    background: palette.border.medium,
                    borderRadius: '3px',
                },
                '::-webkit-scrollbar-thumb:hover': {
                    background: palette.border.strong,
                },
            },
        },

        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    padding: '8px 16px',
                    fontWeight: 600,
                },
                sizeSmall: {
                    padding: '6px 12px',
                    fontSize: '0.6875rem',
                },
                sizeLarge: {
                    padding: '12px 24px',
                    fontSize: '0.875rem',
                },
                contained: {
                    '&:hover': {
                        boxShadow: 'none',
                    },
                },
                outlined: {
                    borderColor: palette.border.medium,
                    '&:hover': {
                        borderColor: palette.primary.main,
                        backgroundColor: alpha(palette.primary.main, 0.08),
                    },
                },
            },
        },

        MuiIconButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                },
                sizeSmall: {
                    padding: 6,
                },
            },
        },

        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    fontWeight: 500,
                    fontSize: '0.6875rem',
                },
                sizeSmall: {
                    height: 22,
                },
            },
        },

        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    border: `1px solid ${palette.border.subtle}`,
                    backgroundImage: 'none',
                },
            },
        },

        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                },
                rounded: {
                    borderRadius: 12,
                },
            },
        },

        MuiTextField: {
            defaultProps: {
                size: 'small',
            },
        },

        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    backgroundColor: palette.background.paper,
                    '& fieldset': {
                        borderColor: palette.border.subtle,
                    },
                    '&:hover fieldset': {
                        borderColor: palette.border.medium,
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: palette.primary.main,
                        boxShadow: `0 0 0 3px ${alpha(palette.primary.main, 0.15)}`,
                    },
                    '&.Mui-disabled': {
                        backgroundColor: palette.background.surface,
                    },
                },
            },
        },

        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontSize: '0.875rem',
                },
                sizeSmall: {
                    fontSize: '0.75rem',
                },
            },
        },

        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: palette.background.elevated,
                    color: palette.text.primary,
                    border: `1px solid ${palette.border.subtle}`,
                    borderRadius: 6,
                    fontSize: '0.6875rem',
                    padding: '6px 10px',
                },
                arrow: {
                    color: palette.background.elevated,
                },
            },
        },

        MuiMenu: {
            styleOverrides: {
                paper: {
                    borderRadius: 10,
                    border: `1px solid ${palette.border.subtle}`,
                    boxShadow: shadows[24],
                },
            },
        },

        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontSize: '0.75rem',
                    padding: '8px 14px',
                    borderRadius: 6,
                    margin: '2px 6px',
                    '&:first-of-type': {
                        marginTop: 6,
                    },
                    '&:last-of-type': {
                        marginBottom: 6,
                    },
                },
            },
        },

        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 16,
                    border: `1px solid ${palette.border.subtle}`,
                },
            },
        },

        MuiTabs: {
            styleOverrides: {
                root: {
                    minHeight: 40,
                },
                indicator: {
                    height: 2,
                    borderRadius: 1,
                },
            },
        },

        MuiTab: {
            styleOverrides: {
                root: {
                    minHeight: 40,
                    padding: '8px 16px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    textTransform: 'none',
                },
            },
        },

        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderColor: palette.border.subtle,
                    padding: '12px 16px',
                    fontVariantNumeric: 'tabular-nums',
                },
                head: {
                    fontWeight: 600,
                    fontSize: '0.6875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: palette.text.secondary,
                    backgroundColor: palette.background.elevated,
                },
            },
        },

        MuiBadge: {
            styleOverrides: {
                badge: {
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.5625rem',
                    fontWeight: 500,
                },
            },
        },

        MuiAvatar: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                },
            },
        },

        MuiDrawer: {
            styleOverrides: {
                paper: {
                    borderRight: `1px solid ${palette.border.subtle}`,
                },
            },
        },

        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: palette.border.subtle,
                },
            },
        },
    };
};

// ============================================================================
// Theme Creation
// ============================================================================

export const createAppTheme = (mode: 'light' | 'dark') => {
    const palette = mode === 'dark' ? darkPalette : lightPalette;

    return createTheme({
        palette,
        typography,
        shadows,
        shape: {
            borderRadius: 8,
        },
        components: getComponentOverrides(mode),
    });
};

// Pre-created themes for quick access
export const darkTheme = createAppTheme('dark');
export const lightTheme = createAppTheme('light');

